import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { compact, flatten, flowRight, isNil, orderBy, map, noop, uniq, uniqBy } from 'lodash';

import CollapsibleText from '@zocdoc/components/src/CollapsibleText';
import CollapsibleTextByLine from '@zocdoc/components/src/CollapsibleTextByLine';
import { ExpandableList, ShowMore, ShowFewer } from '@zocdoc/components/src/ExpandableList';
import { breakpoint } from '@zocdoc/components/src/grid';
import { kievitSlabBook, FontFamily } from '@zocdoc/components/src/typography';
import { Link } from '@zocdoc/components/src/link';
import { grey75 } from '@zocdoc/branding/colors';

import { LOCALIZED_HEADERS, LOCALIZED_SEX, LOCALIZED_STRINGS } from './constants';
import { Sections } from '../../constants';
import BadgeList from '../BadgeList/view';
import InfoTooltip from '../../../../Shared/InfoTooltip/view';
import { InlineInNetworkInsurance } from '../InNetworkInsurance';
import { debounceTooltipTracking } from '../../utils/debounceTooltipTracking';
import withViewportDetection from '../../../../config/viewport';
import { institutesSelector } from '../../../../Shared/utils/institutesAndTitles';

export class BaseProfileTab extends React.PureComponent {
    static propTypes = {
        clickPracticeName: PropTypes.func,
        iBubblesText: PropTypes.object,
        intl: PropTypes.shape({ formatMessage: PropTypes.func }),
        is1Z: PropTypes.bool,
        isDesktop: PropTypes.bool,
        practiceParameters: PropTypes.object,
        provider: PropTypes.object,
        showInstitutes: PropTypes.bool,
        theme: PropTypes.shape({ profile: PropTypes.object }),
        trackClickDataProvidedBy: PropTypes.func,
        trackClickIBubbleBoardCertifications: PropTypes.func,
        trackClickIBubbleHospitalAffiliations: PropTypes.func,
        trackClickIBubbleInsuranceAccepted: PropTypes.func,
        trackClickIBubbleLanguage: PropTypes.func,
        trackClickIBubbleNpi: PropTypes.func,
        trackClickIBubblePracticeNames: PropTypes.func,
        trackClickIBubbleProviderGender: PropTypes.func,
        trackClickIBubbleSpecialty: PropTypes.func,
        trackClickProfessionalStatementShowMore: PropTypes.func,
        trackClickRapidRegistration: PropTypes.func,
        trackClickSchedulingHero: PropTypes.func,
        trackClickSeeYouAgain: PropTypes.func,
        trackClickSpeedyResponse: PropTypes.func,
        usePracticeLinks: PropTypes.bool,
    };

    render() {
        const {
            is1Z,
            provider,
            showInstitutes,
            trackClickProfessionalStatementShowMore,
            trackClickIBubbleBoardCertifications,
            trackClickIBubbleHospitalAffiliations,
            trackClickIBubbleLanguage,
            trackClickIBubbleNpi,
            trackClickIBubblePracticeNames,
            trackClickIBubbleProviderGender,
            trackClickRapidRegistration,
            trackClickSeeYouAgain,
            trackClickSpeedyResponse,
            trackClickSchedulingHero,
            theme,
            intl,
        } = this.props;

        const institutes = showInstitutes ? institutesSelector(provider) : null;

        const sectionDefaultValue = theme.profile.sectionDefaultValue || {};

        const titles = sectionValueOrDefault(
            sectionDefaultValue,
            Sections.Titles,
            flatten(map(institutes, i => i.providerTitles)).filter(Boolean)
        );
        const statement = sectionValueOrDefault(sectionDefaultValue, Sections.Statement, provider.statement);
        const practices = sectionValueOrDefault(sectionDefaultValue, Sections.Practice, provider.practices);
        const hospitalAffiliations = sectionValueOrDefault(
            sectionDefaultValue, Sections.HospitalAffiliations, provider.hospitalAffiliations
        );
        const instituteNames = sectionValueOrDefault(
            sectionDefaultValue,
            Sections.Institutes,
            map(institutes, i => i.instituteName).filter(Boolean)
        );
        const certifications = sectionValueOrDefault(
            sectionDefaultValue, Sections.Certifications, provider.certifications
        );
        const education = sectionValueOrDefault(sectionDefaultValue, Sections.Education, provider.education);
        const memberships = sectionValueOrDefault(sectionDefaultValue, Sections.Memberships, provider.memberships);
        const awards = sectionValueOrDefault(sectionDefaultValue, Sections.Awards, provider.awards);
        const badges = sectionValueOrDefault(sectionDefaultValue, Sections.Badges, provider.badges);
        const sex = numericSectionValueOrDefault(sectionDefaultValue, Sections.Sex, provider.sex);
        const languages = sectionValueOrDefault(sectionDefaultValue, Sections.Languages, provider.languages);
        const npi = sectionValueOrDefault(sectionDefaultValue, Sections.NPI, provider.npi);

        const badgesString = is1Z ? Sections.Badges1Z : Sections.Badges;

        return (
            <React.Fragment>
                {this.maybeRenderSection({
                    string: Sections.Titles,
                    test: titles,
                    children: this.createStringList(titles),
                })}

                {this.maybeRenderSection({
                    string: Sections.Statement,
                    test: statement,
                    children: is1Z
                        ? (
                            <StatementText
                                onClickToggle={trackClickProfessionalStatementShowMore}
                                prewrapped
                            >
                                {statement}
                            </StatementText>
                        )
                        : (
                            <CollapsibleText
                                collapseWords={75}
                                itemProp="description"
                                maxWords={100}
                                onClickShowMore={trackClickProfessionalStatementShowMore}
                                prewrapped
                                showLessEnabled={false}
                                showMoreHelperText={intl.formatMessage(LOCALIZED_STRINGS.ReadMore)}
                            >
                                {statement}
                            </CollapsibleText>
                        ),
                })}

                {this.renderSpecialtiesAndInNetworkInsurance()}

                {this.maybeRenderSection({
                    string: Sections.Practice,
                    test: practices,
                    children: this.practiceSection(practices),
                    tooltipTrackingFn: trackClickIBubblePracticeNames,
                })}

                {this.maybeRenderSection({
                    string: Sections.HospitalAffiliations,
                    test: hospitalAffiliations,
                    children: this.createStringList(hospitalAffiliations),
                    tooltipTrackingFn: trackClickIBubbleHospitalAffiliations,
                })}

                {this.maybeRenderSection({
                    string: Sections.Institutes,
                    test: showInstitutes && instituteNames,
                    children: this.createStringList(instituteNames),
                })}

                {this.maybeRenderSection({
                    string: Sections.Certifications,
                    test: certifications,
                    children: this.createStringList(certifications),
                    tooltipTrackingFn: trackClickIBubbleBoardCertifications,
                })}

                {this.maybeRenderSection({
                    string: Sections.Education,
                    test: education,
                    children: this.createEducationList(education),
                })}

                {this.maybeRenderSection({
                    string: Sections.Memberships,
                    test: memberships,
                    children: this.createExpandableList(memberships),
                })}

                {this.maybeRenderSection({
                    string: Sections.Awards,
                    test: awards,
                    children: this.createExpandableList(awards, { itemProp: 'award' }),
                })}

                {this.maybeRenderSection({
                    string: badgesString,
                    test: badges,
                    children: (
                        <BadgeList
                            badges={badges}
                            trackClickRapidRegistration={trackClickRapidRegistration}
                            trackClickSchedulingHero={trackClickSchedulingHero}
                            trackClickSeeYouAgain={trackClickSeeYouAgain}
                            trackClickSpeedyResponse={trackClickSpeedyResponse}
                        />),
                })}

                {this.maybeRenderSection({
                    string: Sections.Languages,
                    test: languages,
                    children: this.createExpandableList(languages),
                    tooltipTrackingFn: trackClickIBubbleLanguage,
                })}

                {this.shouldRenderSex(sex) && this.maybeRenderSection({
                    string: Sections.Sex,
                    test: sex,
                    children: this.renderSex(sex),
                    tooltipTrackingFn: trackClickIBubbleProviderGender,
                })}

                {this.maybeRenderSection({
                    string: Sections.NPI,
                    test: npi,
                    children: <p itemProp="identifier">{npi}</p>,
                    tooltipTrackingFn: trackClickIBubbleNpi,
                })}

                {this.maybeRenderDataProvidedBy()}
            </React.Fragment>
        );
    }

    renderSpecialtiesAndInNetworkInsurance = () => {
        const {
            is1Z,
            isDesktop,
            theme,
            provider,
            trackClickIBubbleSpecialty,
            trackClickIBubbleInsuranceAccepted,
        } = this.props;

        const sectionDefaultValue = theme.profile.sectionDefaultValue || {};

        const inNetworkInsurance = sectionValueOrDefault(
            sectionDefaultValue, Sections.InNetworkInsurance, provider.inNetworkInsurance
        );
        const inNetworkInsuranceString = is1Z && !isDesktop
            ? Sections.InNetworkInsuranceMobile1Z : Sections.InNetworkInsurance;
        const specialties = sectionValueOrDefault(sectionDefaultValue, Sections.Specialties, provider.specialties);

        const specialtiesComponent = this.maybeRenderSection({
            string: Sections.Specialties,
            test: specialties,
            children: this.createIdNameList(specialties),
            tooltipTrackingFn: trackClickIBubbleSpecialty,
        });

        const inNetworkInsurancesComponent = this.maybeRenderSection({
            string: inNetworkInsuranceString,
            test: inNetworkInsurance,
            children: (
                <InlineInNetworkInsurance
                    inNetworkInsurance={inNetworkInsurance}
                    provider={provider}
                />
            ),
            tooltipTrackingFn: trackClickIBubbleInsuranceAccepted,
        });

        if (is1Z) {
            return (
                <React.Fragment>
                    {inNetworkInsurancesComponent}
                    {specialtiesComponent}
                </React.Fragment>
            );
        } else {
            return (
                <React.Fragment>
                    {specialtiesComponent}
                    {inNetworkInsurancesComponent}
                </React.Fragment>
            );
        }
    };

    shouldRenderSex = sex => {
        return (sex === 1 || sex === 2 || typeof sex === 'string');
    };

    renderSex = sex => {
        const { intl } = this.props;
        const text = LOCALIZED_SEX[sex] ? intl.formatMessage(LOCALIZED_SEX[sex]) : sex;
        return <p>{text}</p>;
    };

    practiceSection = practices => {
        const { clickPracticeName, practiceParameters, usePracticeLinks } = this.props;
        const Practice = usePracticeLinks ? Link : RegularText;

        const paramString = compact(map(practiceParameters, (val, key) => !isNil(val) && `${key}=${val}`)).join('&');

        if (practices && typeof practices === 'string') {
            return <div>{practices}</div>;
        } else if (practices) {
            return (
                <ul>
                    {uniqBy(practices, 'id').map(practice => (
                        <Item key={practice.id}>
                            <Practice
                                beforeClick={() => clickPracticeName(practice.name)}
                                data-test="profile-practice-link"
                                data-uem-id="practiceName-link"
                                href={paramString ? `${practice.url}?${paramString}` : practice.url}
                                itemProp="memberOf"
                            >
                                {practice.name}
                            </Practice>
                        </Item>
                    ))}
                </ul>
            );
        }
    };

    maybeRenderSection = ({
        string, test, children, tooltipTrackingFn = noop,
    }) => {
        const { iBubblesText } = this.props.theme.profile;
        const allLocalizedTooltips = localizedTooltips(iBubblesText);

        const shouldRender = test && (!Array.isArray(test) || test.length);

        if (shouldRender) {
            let tooltip;

            const iBubbleMessage = allLocalizedTooltips[string];
            const iBubbleTextIsDefined = iBubbleMessage && iBubbleMessage.defaultMessage;

            if (iBubbleTextIsDefined) {
                const text = this.props.intl.formatMessage(iBubbleMessage);

                tooltip = (
                    <InfoTooltip
                        alt={text}
                        onDisplayTooltip={debounceTooltipTracking(tooltipTrackingFn)}
                    >
                        {text}
                    </InfoTooltip>
                );
            } else {
                tooltip = null;
            }

            return (
                <ProfileSection data-test={`${string}-section`}>
                    <SectionHeader
                        text={string}
                        tooltip={tooltip}
                    />
                    {children}
                </ProfileSection>
            );
        }
    };

    createIdNameList = (list, attrs) => {
        if (list) {
            const items = uniqBy(list, 'id').map(({ id, name }) => (
                <Item
                    key={id}
                    {...attrs}
                >
                    {name}
                </Item>
            ));

            return (
                <ul>
                    {items}
                </ul>
            );
        }
    };

    createEducationList = educationList => {
        return (
            <ul>
                {map(orderBy(educationList, 'endDate', 'desc'), (education, index) => (
                    <Item key={index} >
                        <span>{education.instituteName}{education.degreeType ? ` (${education.degreeType})` : ''}</span>
                        <EducationDate>{year(education.startDate, education.endDate)}</EducationDate>
                    </Item>
                ))}
            </ul>
        );
    };

    createStringList = (list, attrs) => {
        if (list && typeof list === 'string') {
            return <div>{list}</div>;
        } else if (list) {
            const items = uniq(list).map((name, idx) => (
                <Item
                    key={idx}
                    {...attrs}
                >
                    {name}
                </Item>
            ));

            return (
                <ul>
                    {items}
                </ul>
            );
        }
    };

    createExpandableList = list => {
        if (list && typeof list === 'string') {
            return <div>{list}</div>;
        } else if (list) {
            return (
                <ExpandableList>
                    {uniq(list).map((name, idx) => <Item key={idx}>{name}</Item>)}
                    <ShowMore>
                        <RegularText><FormattedMessage {...LOCALIZED_STRINGS.ShowMore} /></RegularText>
                    </ShowMore>
                    <ShowFewer>
                        <RegularText><FormattedMessage {...LOCALIZED_STRINGS.ShowFewer} /></RegularText>
                    </ShowFewer>
                </ExpandableList>
            );
        }
    };

    maybeRenderDataProvidedBy = () => {
        const { provider, trackClickDataProvidedBy } = this.props;
        if (provider.isPreview && provider.dataProvidedBy) {
            return (
                <ProfileSection data-test="data-provided-by-section">
                    <p>
                        <FormattedMessage {...LOCALIZED_STRINGS.DataProvidedBy} />
                        {provider.dataProvidedByUrl ? (
                            <Link
                                href={provider.dataProvidedByUrl}
                                onClick={trackClickDataProvidedBy}
                            >
                                {provider.dataProvidedBy}
                            </Link>
                        ) : <span data-test="data-provided-by-span">{provider.dataProvidedBy}</span>}
                    </p>
                </ProfileSection>
            );
        }
    }
}

const localizedTooltips = iBubblesText => defineMessages({
    [Sections.Badges]: {
        id: 'Profile.Components.ProfileTab.Badges.Tooltip',
        defaultMessage: iBubblesText[Sections.Badges],
    },
    [Sections.Badges1Z]: {
        id: 'Profile.Components.ProfileTab.Badges.Tooltip',
        defaultMessage: iBubblesText[Sections.Badges],
    },
    [Sections.Certifications]: {
        id: 'Profile.Components.ProfileTab.Certifications.Tooltip',
        defaultMessage: iBubblesText[Sections.Certifications],
    },
    [Sections.HospitalAffiliations]: {
        id: 'Profile.Components.ProfileTab.HospitalAffiliations.Tooltip',
        defaultMessage: iBubblesText[Sections.HospitalAffiliations],
    },
    [Sections.InNetworkInsurance]: {
        id: 'Profile.Components.ProfileTab.InNetworkInsurance.Tooltip',
        defaultMessage: iBubblesText[Sections.InNetworkInsurance],
    },
    [Sections.Languages]: {
        id: 'Profile.Components.ProfileTab.Languages.Tooltip',
        defaultMessage: iBubblesText[Sections.Languages],
    },
    [Sections.NPI]: {
        id: 'Profile.Components.ProfileTab.NPI.Tooltip',
        defaultMessage: iBubblesText[Sections.NPI],
    },
    [Sections.Practice]: {
        id: 'Profile.Components.ProfileTab.Practice.Tooltip',
        defaultMessage: iBubblesText[Sections.Practice],
    },
    [Sections.Sex]: {
        id: 'Profile.Components.ProfileTab.Sex.Tooltip',
        defaultMessage: iBubblesText[Sections.Sex],
    },
    [Sections.Specialties]: {
        id: 'Profile.Components.ProfileTab.Specialties.Tooltip',
        defaultMessage: iBubblesText[Sections.Specialties],
    },
});

const genericSectionValueOrDefault = hasNoValueTest => (sectionDefaultValue, section, actualValue) => {
    const hasNoValue = !actualValue || hasNoValueTest(actualValue);
    if (hasNoValue && sectionDefaultValue[section]) {
        return sectionDefaultValue[section];
    } else {
        return actualValue;
    }
};

export const sectionValueOrDefault = genericSectionValueOrDefault(value => Array.isArray(value) && value.length < 1);

export const numericSectionValueOrDefault = genericSectionValueOrDefault(value => isNil(value));

export function year(startDate, endDate) {
    if (startDate && endDate) {
        const startYear = startDate.split('-')[0];
        const endYear = endDate.split('-')[0];
        return startYear === endYear ? startYear : `${startYear} - ${endYear}`;
    }
}

const contentFont = kievitSlabBook(16, 24);
const desktopContentFont = kievitSlabBook(18, 24);

const ProfileSection = styled.section`
    margin-top: 28px;
    line-height: 24px;

    ${breakpoint.medium} {
        margin-top: 36px;
        line-height: 28px;
    }
`;

const HeaderTextH3Style = styled.h3`
    font: 18px/24px ${FontFamily.SharpSansBold};
    margin-bottom: 8px;

    ${breakpoint.medium} {
        font-size: 20px;
    }
`;

const SectionHeader = ({
    children, text, tooltip,
}) => (
    <HeaderTextH3Style>
        <FormattedMessage {...LOCALIZED_HEADERS[text]} />
        {tooltip}
        {children}
    </HeaderTextH3Style>
);

const RegularText = styled.span`
    ${contentFont}
    ${breakpoint.medium} {
        ${desktopContentFont}
    }
`;

const Item = styled.li`
    margin-bottom: 8px;
    ${breakpoint.medium} {
        font: 18px/24px ${FontFamily.KievitSlabBook};
    }
`;

const StatementText = styled(CollapsibleTextByLine)`
    white-space: pre-line;
`;

const EducationDate = styled.span`
    color: ${grey75};
    font-size: 14px;
    font-family: ${FontFamily.SharpSansSemibold};
    margin-left: 8px;
`;

export default flowRight(
    injectIntl,
    withViewportDetection
)(BaseProfileTab);
